import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAgentState } from '@/lib/agentState';
import { Config } from '@/lib/api';
import { useSoftphone } from '@/store/softphone';
import { useShortcuts, type ShortcutBinding } from '@/lib/useShortcuts';
import { Topbar } from './Topbar';
import { Sidebar } from './Sidebar';
import { UpdateBanner } from '@/components/UpdateBanner';
import { setWindowSize } from '@/lib/desktop';
import { useAuth } from '@/store/auth';
import { SessionWs } from '@/lib/session-ws';
import { wsBase, logActivityBeacon } from '@/lib/api';
import { ForceLogoutModal } from '@/components/ForceLogoutModal';
import { CallAudio } from '@/components/softphone/CallAudio';
import { TransferSheet } from '@/components/softphone/TransferSheet';
import { QuickDialPopover } from '@/components/softphone/QuickDialPopover';
import { BreakReasonPopover } from '@/components/softphone/BreakReasonPopover';
import { NotificationDrawer } from '@/components/shell/NotificationDrawer';
import { ConnectionBanner } from '@/components/shell/ConnectionBanner';
import { useSipHealthRecovery } from '@/lib/useSipHealth';
import { connReset } from '@/store/connection';

/**
 * ArkConnect uygulama kabuğu — L-shape unified chrome.
 *
 *  ┌──────────────────────────────────┐
 *  │ TOPBAR (56px)                    │ ← state-aware chrome bg
 *  ├──────┬───────────────────────────┤
 *  │      │                           │
 *  │ SIDE │     MAIN (rounded-tl)     │ ← beyaz alan, chrome içinden çıkar
 *  │ BAR  │                           │
 *  │ 78px │                           │
 *  └──────┴───────────────────────────┘
 *
 * Klavye kısayolları ve modal'lar (IncomingCallModal, Audio/Shortcuts help)
 * shell seviyesinde mount edilir — sayfa değişimi durumlarına dayanıklı.
 */
export function AppShell() {
  const agentState = useAgentState();
  const sp = useSoftphone();
  const { state, call, queueIn, queueAction, init: spInit, client: spClient } = sp;
  const { data: pbxCfg } = useQuery({ queryKey: ['pbx-config'], queryFn: Config.pbx });
  const [_, setRender] = useState(0); // setState helpers for callbacks
  void _;
  const { token, user, logout } = useAuth();
  const qc = useQueryClient();
  const [forceLogout, setForceLogout] = useState<{ reason: string } | null>(null);
  const isLeader = user?.role === 'leader' || user?.role === 'admin';
  /** Aktif SessionWs — call_event publish'i (supervisor monitoring) için ref'te tutulur. */
  const wsRef = useRef<SessionWs | null>(null);

  // ─── Supervisor monitoring: agent'ın O ANKİ canlı çağrı durumunu backend'e
  // push'la (metadata-only, SES YOK). Backend 'calls:live' topic'ine yayar →
  // lider "Şu an konuşanlar" board'u. getState() ile okur (closure stale olmasın).
  const publishCall = useCallback((ws: SessionWs | null) => {
    if (!ws) return;
    const s = useSoftphone.getState();
    const onCall = s.state === 'in_call' || s.state === 'ringing' || s.state === 'calling';
    ws.send({
      event: 'call_event',
      data: onCall && s.call
        ? {
            onCall: true,
            state: s.state,
            direction: s.call.direction,
            peer: s.call.peer,
            peerName: s.call.displayName ?? null,
            callId: s.call.callId ?? null,
            startedAt: s.call.startedAt ?? null,
            answeredAt: s.call.answeredAt ?? null,
            onHold: s.onHold,
            muted: s.muted,
          }
        : { onCall: false },
    });
  }, []);

  // ─── Single-session enforcement + canlı push — connect to backend /ws.
  // Another login on the same account makes backend close THIS socket with
  // code 4002 and push a force_logout message. We show a modal and log out.
  // Aynı kanal: notification_new → bildirim invalidate; agents:live (leader) →
  // canlı agent tablosu cache'i (state poller push'lar).
  useEffect(() => {
    if (!token) return;
    const ws = new SessionWs(wsBase(), token);
    const unsubForce = ws.on('force_logout', (msg) => {
      console.warn('[session-ws] force_logout', msg);
      setForceLogout({ reason: msg.reason });
    });
    const unsubClose = ws.on('close', (code) => {
      if (code === 4002) setForceLogout({ reason: 'replaced' });
    });
    const unsubNotif = ws.on('notification_new', () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    });
    // Sunucu-otoriteli süre snapshot'ı (Faz 2): heartbeat'le gelen anchor'ları
    // doğrudan query cache'e yaz → useAgentDurations REST poll yerine bundan
    // beslenir (liveness kanalı = veri kanalı, ayrışamaz).
    const unsubDurations = ws.on('durations', (d) => {
      qc.setQueryData(['agent-durations'], d);
    });
    // Leader/admin → canlı agent tablosu push'una abone ol. Gelen satırları
    // doğrudan query cache'ine yaz (refetch yok, anında güncellenir).
    const unsubAgents = isLeader
      ? ws.subscribe('agents:live', (rows) => {
          if (Array.isArray(rows)) qc.setQueryData(['leader-agents'], rows);
        })
      : undefined;
    // Lider/admin → "Şu an konuşanlar" canlı çağrı board'u (supervisor monitoring).
    const unsubLive = isLeader
      ? ws.subscribe('calls:live', (rows) => {
          if (Array.isArray(rows)) qc.setQueryData(['live-calls'], rows);
        })
      : undefined;
    // Reconnect sonrası kendi canlı çağrı durumunu yeniden yolla (gateway kopuşta
    // snapshot'ı sildiği için board'da kaybolmasın).
    const unsubOpen = ws.on('open', () => publishCall(ws));
    wsRef.current = ws;
    ws.connect();
    return () => {
      unsubForce();
      unsubClose();
      unsubNotif();
      unsubDurations();
      unsubAgents?.();
      unsubLive?.();
      unsubOpen();
      wsRef.current = null;
      ws.close();
      connReset(); // logout/unmount → bağlantı durumunu temizle
    };
  }, [token, qc, isLeader, publishCall]);

  // Softphone çağrı durumu (state/call/hold/mute) değiştikçe canlı snapshot'ı
  // backend'e yolla → lider board'u anlık güncellenir.
  useEffect(() => {
    publishCall(wsRef.current);
  }, [state, call, sp.onHold, sp.muted, publishCall]);

  const handleForceLogoutAck = () => {
    setForceLogout(null);
    void logout();
  };

  // ─── Initialise softphone (Janus connect + SIP register) once auth+pbx are ready
  useEffect(() => {
    if (spClient || !pbxCfg) return;
    console.log('[appshell] starting softphone init…');
    spInit().catch((e) => console.error('[appshell] softphone init failed', e));
  }, [spClient, pbxCfg, spInit]);

  // ─── Logout / kullanıcı değişimi: AppShell unmount olunca softphone'u YIK.
  // Yoksa softphone Zustand singleton olduğu için eski kullanıcının client'ı
  // (örn. agent 9998) hayatta kalıyor; yeni login'de (örn. lider 1099) init
  // `if (spClient) return` ile re-init'i atlıyor → yeni kullanıcı ESKİ extension'ın
  // SIP kaydını miras alıyor (sip-health mismatch, register flap, oturum çakışması).
  // Destroy → sonraki login taze init eder (yeni kullanıcının credentials'ı).
  useEffect(() => {
    return () => {
      console.log('[appshell] unmount → softphone destroy (sonraki login temiz başlasın)');
      useSoftphone.getState().destroy();
    };
  }, []);

  // ─── Mount SIP health probe — polls 3CX every 30s and auto-reregisters
  // when local Janus state diverges from real PBX registration status.
  // MUST be mounted exactly once (here) — Topbar reads the same query via
  // useSipHealthQuery() without triggering recovery again.
  useSipHealthRecovery();

  // ─── Online (Bağlı) süresi: sekme/uygulama kapanışında agent_offline logla
  // (keepalive beacon). janus_registered connect marker, bu disconnect marker —
  // durations.service gerçek online toplamını bu çiftlerden hesaplar.
  useEffect(() => {
    if (!token) return;
    const onUnload = () => logActivityBeacon('agent_offline', { reason: 'unload' });
    window.addEventListener('pagehide', onUnload);
    return () => window.removeEventListener('pagehide', onUnload);
  }, [token]);

  // ─── Tauri: login sonrası pencereyi tam workspace boyutuna büyüt.
  // Login.tsx 540×720 kompakt boyuta indiriyor; AppShell mount olduğu an
  // (kullanıcı giriş yapmış) genişletip ortalıyoruz.
  useEffect(() => { void setWindowSize(1320, 820); }, []);

  // ─── Global keyboard shortcuts ─────────────────────────────────
  const shortcuts: ShortcutBinding[] = useMemo(() => [
    {
      display: 'Ctrl+Shift+A',
      label: 'Çağrıyı cevapla',
      key: 'a', ctrl: true, shift: true,
      action: () => {
        if (state === 'ringing' && call?.direction === 'inbound') sp.accept();
      },
    },
    {
      display: 'Ctrl+Shift+D',
      label: 'Çağrıyı reddet',
      key: 'd', ctrl: true, shift: true,
      action: () => {
        if (state === 'ringing' && call?.direction === 'inbound') sp.reject();
      },
    },
    {
      display: 'Ctrl+Shift+H',
      label: 'Çağrıyı kapat',
      key: 'h', ctrl: true, shift: true,
      action: () => {
        if (state === 'in_call' || state === 'calling' || state === 'ringing') sp.hangup();
      },
    },
    {
      display: 'Ctrl+Shift+M',
      label: 'Mikrofonu sustur / aç',
      key: 'm', ctrl: true, shift: true,
      action: () => { if (state === 'in_call') sp.toggleMute(); },
    },
    {
      display: 'Ctrl+Shift+P',
      label: 'Beklet / devam',
      key: 'p', ctrl: true, shift: true,
      action: () => { if (state === 'in_call') sp.toggleHold(); },
    },
    {
      display: 'Ctrl+Shift+Q',
      label: 'Kuyruğa katıl / çık',
      key: 'q', ctrl: true, shift: true,
      action: () => {
        if (state !== 'registered' || queueAction) return;
        const code = queueIn ? pbxCfg?.queue_codes.logout : pbxCfg?.queue_codes.login;
        if (code) sp.triggerQueueAction(queueIn ? 'logout' : 'login', code);
      },
    },
  ], [state, call, queueIn, queueAction, sp, pbxCfg]);

  useShortcuts(shortcuts);

  // Expose agent state to CSS for [data-arkstate=...] selectors
  useEffect(() => {
    document.documentElement.setAttribute('data-arkstate', agentState);
    setRender((n) => n + 1); // ensure re-render captures attribute change in dev
    return () => { document.documentElement.removeAttribute('data-arkstate'); };
  }, [agentState]);

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <ConnectionBanner />
      <UpdateBanner />
      <Topbar />

      <div className="flex-1 flex overflow-hidden">
        <Sidebar />
        <main className="flex-1 bg-slate-50 rounded-tl-[28px] overflow-hidden shadow-[inset_1px_1px_0_rgba(255,255,255,0.6)]">
          <div className="h-full overflow-y-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {forceLogout && (
        <ForceLogoutModal reason={forceLogout.reason} onAck={handleForceLogoutAck} />
      )}

      <CallAudio />
      <TransferSheet />
      <QuickDialPopover />
      <BreakReasonPopover />
      <NotificationDrawer />
    </div>
  );
}
